import React, { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { poetryPieces } from '../data/poetry';
import Header from './Header';
import Footer from './Footer';
import FloatingIcons from './FloatingIcons';

function PoetryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const poem = id ? poetryPieces.find(p => p.id === id) : null;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Calculate previous and next poem
  const { prevPoem, nextPoem } = useMemo(() => {
    if (!id) return { prevPoem: null, nextPoem: null };

    const currentIndex = poetryPieces.findIndex(p => p.id === id);
    if (currentIndex === -1) return { prevPoem: null, nextPoem: null };

    // Previous = older poem (higher index, later in array)
    const prevPoem = currentIndex < poetryPieces.length - 1 ? poetryPieces[currentIndex + 1] : null;
    // Next = newer poem (lower index, earlier in array)
    const nextPoem = currentIndex > 0 ? poetryPieces[currentIndex - 1] : null;

    return { prevPoem, nextPoem };
  }, [id]);

  if (!poem) {
    return (
      <div className="App">
        <FloatingIcons />
        <Header />
        <div className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>
          <h2>Poetry Not Found</h2>
          <Link to="/poetry" className="cta-button primary" style={{ marginTop: '24px', display: 'inline-block' }}>
            Back to Poetry
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Format date
  const formattedDate = poem.date === 'TBD' ? 'Date TBD' : new Date(poem.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="App">
      <FloatingIcons />
      <Header />
      <section className="career-detail-section blog-detail-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/poetry" className="career-back-link blog-back-link">
              <ArrowLeft size={20} />
              <span>Back to Poetry</span>
            </Link>
          </motion.div>

          <motion.div
            className="blog-detail-header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="blog-detail-title-section poetry-detail-title-section">
              <h1 className="blog-detail-title">{poem.title}</h1>
              <div className="blog-detail-meta poetry-detail-meta">
                <span className="blog-detail-date">{formattedDate}</span>
                {poem.tags && poem.tags.length > 0 && (
                  <div className="blog-detail-tags">
                    {poem.tags.map((tag) => (
                      <span key={tag} className="blog-tag">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {poem.logo && (
            <motion.div
              className="poetry-detail-logo-wrapper"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img src={poem.logo} alt={poem.title} />
            </motion.div>
          )}

          <motion.div
            className="blog-detail-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="blog-markdown-content">
              <ReactMarkdown>{poem.content}</ReactMarkdown>
            </div>
          </motion.div>

          {/* Previous/Next Navigation */}
          {(nextPoem || prevPoem) && (
            <motion.div
              className="career-detail-navigation blog-detail-navigation"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {prevPoem ? (
                <Link to={`/poetry/${prevPoem.id}`} className="career-nav-link career-nav-prev">
                  <ChevronLeft size={20} />
                  <div className="career-nav-content">
                    <span className="career-nav-label">Previous</span>
                    <span className="career-nav-name">{prevPoem.title}</span>
                  </div>
                </Link>
              ) : (
                <div style={{ flex: 1 }}></div>
              )}

              {nextPoem ? (
                <Link to={`/poetry/${nextPoem.id}`} className="career-nav-link career-nav-next">
                  <div className="career-nav-content">
                    <span className="career-nav-label">Next</span>
                    <span className="career-nav-name">{nextPoem.title}</span>
                  </div>
                  <ChevronRight size={20} />
                </Link>
              ) : (
                <div style={{ flex: 1 }}></div>
              )}
            </motion.div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default PoetryDetailPage;

